import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Grid3x3, RadioTower } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PARTNER_BRANDS } from "../lib/partnersData";

const BROWSE_BRANDS_LABEL = {
  pt: "Explore todas as marcas representadas",
  en: "Browse all represented brands",
  es: "Explora todas las marcas representadas",
};

const pick = (map, lang) => map?.[lang] || map?.pt || "";

// Featured TVCabSat-built services that don't belong to any partner brand
// but should still surface in the mega-menu (e.g. tower construction).
const TVCABSAT_SERVICES = {
  id: "tvcabsat-services",
  color: "#0ea5e9",
  name: {
    pt: "TVCabSat — Serviços",
    en: "TVCabSat — Services",
    es: "TVCabSat — Servicios",
  },
  tagline: {
    pt: "Engenharia, instalação e manutenção feita pela TVCabSat.",
    en: "Engineering, installation and maintenance by TVCabSat.",
    es: "Ingeniería, instalación y mantenimiento por TVCabSat.",
  },
  items: [
    {
      id: "mastros-torres",
      route: "/infraestruturas",
      title: {
        pt: "Torres de Telecomunicações e Mastros",
        en: "Telecom Towers & Masts",
        es: "Torres de Telecomunicaciones y Mástiles",
      },
      meta: {
        pt: "6m–30m",
        en: "6m–30m",
        es: "6m–30m",
      },
    },
  ],
};

/**
 * Mega-menu catalog: lists every represented brand and its groups/categories
 * as deep links (e.g. `/#cisco:switching`). Clicking a link navigates to the
 * home-page Partners section, scrolls to the brand showcase and auto-selects
 * the matching tab via BrandShowcase's hashchange listener.
 */
export const CatalogMenu = () => {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const closeAndGo = (hash) => {
    setOpen(false);
    navigate(`/#${hash}`);
    // Force the BrandShowcase hashchange listener to react even when
    // staying on the same page with the same brand id.
    setTimeout(() => {
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }, 50);
  };

  const closeAndRoute = (route) => {
    setOpen(false);
    navigate(route);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Close the mega-panel when the user scrolls the page or resizes the
  // viewport. The panel is `position: fixed`, so without this it stays
  // visually pinned while the underlying content scrolls behind it.
  useEffect(() => {
    if (!open) return undefined;
    let lastY = window.scrollY;
    const onScroll = () => {
      // Tolerate tiny scroll jitter (mobile inertia, focus jumps)
      if (Math.abs(window.scrollY - lastY) > 8) setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    // Lock background scroll while the panel is open so the user can't
    // scroll the page underneath the fixed overlay.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={rootRef} className="relative" data-testid="catalog-menu-root">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="catalog-menu-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        className="nav-link inline-flex origin-bottom items-center gap-1.5 whitespace-nowrap text-base font-semibold text-slate-700 transition-transform duration-200 ease-out hover:scale-[1.2] hover:text-slate-900 lg:text-lg"
      >
        <Grid3x3 className="h-4 w-4" strokeWidth={2.2} />
        {t.nav.catalog}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2.5}
        />
      </button>

      {open && (
        <>
          {/* Backdrop to visually separate mega-panel */}
          <div
            aria-hidden
            className="fixed inset-x-0 top-[140px] bottom-0 -z-10 bg-slate-900/20 backdrop-blur-[2px]"
            onClick={close}
          />
          <div
            data-testid="catalog-menu-panel"
            className="fixed left-1/2 top-[130px] z-40 w-[min(1280px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-2xl border border-slate-200 bg-white/98 shadow-2xl ring-1 ring-black/5 backdrop-blur-xl"
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-[#d8e8f5] to-blue-50/50 px-6 py-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-blue-600">
                {t.nav.catalog}
              </div>
              <div className="font-heading mt-1 text-lg font-bold text-slate-900">
                {BROWSE_BRANDS_LABEL[lang] || BROWSE_BRANDS_LABEL.pt}
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {PARTNER_BRANDS.map((brand) => (
                  <div
                    key={brand.id}
                    data-testid={`catalog-brand-${brand.id}`}
                    className="rounded-xl border border-slate-100 bg-white p-4 transition-all hover:border-blue-200 hover:shadow-md"
                  >
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => closeAndGo(brand.id)}
                        className="font-heading text-base font-black uppercase tracking-wide text-slate-900 transition-colors hover:text-blue-700"
                        style={{ color: brand.color }}
                      >
                        {brand.name}
                      </button>
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: brand.color }}
                      />
                    </div>
                    <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-500">
                      {pick(brand.tagline, lang)}
                    </p>
                    <ul className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
                      {brand.groups.map((g) => (
                        <li key={g.id}>
                          <button
                            type="button"
                            onClick={() => closeAndGo(`${brand.id}:${g.id}`)}
                            data-testid={`catalog-link-${brand.id}-${g.id}`}
                            className="group flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-slate-700 transition-all hover:bg-blue-50 hover:text-blue-700"
                          >
                            <span className="truncate">{pick(g.title, lang)}</span>
                            <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider text-slate-400 group-hover:text-blue-600">
                              {g.products?.length || 0}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {/* Featured TVCabSat-built services (towers, etc.) */}
                <div
                  data-testid={`catalog-brand-${TVCABSAT_SERVICES.id}`}
                  className="rounded-xl border border-blue-200 bg-gradient-to-br from-sky-50 via-[#d8e8f5] to-blue-50 p-4 ring-1 ring-blue-100 transition-all hover:border-blue-300 hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => closeAndRoute("/servicos/mastros-torres")}
                      className="font-heading text-base font-black uppercase tracking-wide text-slate-900 transition-colors hover:text-blue-700"
                      style={{ color: TVCABSAT_SERVICES.color }}
                    >
                      {pick(TVCABSAT_SERVICES.name, lang)}
                    </button>
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: TVCABSAT_SERVICES.color }}
                    />
                  </div>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-500">
                    {pick(TVCABSAT_SERVICES.tagline, lang)}
                  </p>
                  <ul className="mt-3 space-y-1.5 border-t border-blue-100 pt-3">
                    {TVCABSAT_SERVICES.items.map((it) => (
                      <li key={it.id}>
                        <button
                          type="button"
                          onClick={() => closeAndRoute(it.route)}
                          data-testid={`catalog-link-${TVCABSAT_SERVICES.id}-${it.id}`}
                          className="group flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-slate-700 transition-all hover:bg-blue-50 hover:text-blue-700"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <RadioTower
                              className="h-3.5 w-3.5 shrink-0 text-sky-500"
                              strokeWidth={2.2}
                            />
                            <span className="truncate">{pick(it.title, lang)}</span>
                          </span>
                          <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider text-slate-400 group-hover:text-blue-600">
                            {pick(it.meta, lang)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CatalogMenu;
